import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { ArrowLeft, ShoppingCart, Trash2, Plus, Minus, Package } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Footer from './Footer';

const CartPage = ({ navigateHome, navigateToProducts, cart, setCart, clearCart }) => {
  const { isAuthenticated, getIdToken } = useAuth();
  const [loadingCheckout, setLoadingCheckout] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  
  // Address form state
  const [billingAddress, setBillingAddress] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address1: '',
    address2: '',
    city: '',
    state: '', // Will be state for USA or province for Canada
    zipCode: '', // Will be ZIP for USA or postal code for Canada
    country: 'United States'
  });
  
  const [shippingAddress, setShippingAddress] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address1: '',
    address2: '',
    city: '',
    state: '', // Will be state for USA or province for Canada
    zipCode: '', // Will be ZIP for USA or postal code for Canada
    country: 'United States'
  });
  
  const [sameAsShipping, setSameAsShipping] = useState(true);
  const [addressErrors, setAddressErrors] = useState({});

  // Address validation
  const validateAddress = (address, type) => {
    const errors = {};
    const isUSA = address.country === 'United States';
    
    if (!address.firstName.trim()) errors[`${type}FirstName`] = 'First name is required';
    if (!address.lastName.trim()) errors[`${type}LastName`] = 'Last name is required';
    if (!address.email.trim()) errors[`${type}Email`] = 'Email is required';
    if (!address.phone.trim()) errors[`${type}Phone`] = 'Phone number is required';
    if (!address.address1.trim()) errors[`${type}Address1`] = 'Address is required';
    if (!address.city.trim()) errors[`${type}City`] = 'City is required';
    if (!address.state.trim()) {
      errors[`${type}State`] = isUSA ? 'State is required' : 'Province is required';
    }
    if (!address.zipCode.trim()) {
      errors[`${type}ZipCode`] = isUSA ? 'ZIP code is required' : 'Postal code is required';
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (address.email && !emailRegex.test(address.email)) {
      errors[`${type}Email`] = 'Please enter a valid email';
    }
    
    // Postal code validation for Canada (format: A1A 1A1)
    if (!isUSA && address.zipCode) {
      const canadaPostalRegex = /^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/;
      if (!canadaPostalRegex.test(address.zipCode)) {
        errors[`${type}ZipCode`] = 'Please enter a valid postal code (A1A 1A1)';
      }
    }
    
    // ZIP code validation for USA (5 digits or 5+4)
    if (isUSA && address.zipCode) {
      const usaZipRegex = /^\d{5}(-\d{4})?$/;
      if (!usaZipRegex.test(address.zipCode)) {
        errors[`${type}ZipCode`] = 'Please enter a valid ZIP code (12345 or 12345-6789)';
      }
    }
    
    return errors;
  };

  // Handle same as shipping checkbox
  const handleSameAsShipping = (checked) => {
    setSameAsShipping(checked);
    if (checked) {
      setBillingAddress({ ...shippingAddress });
    } else {
      // Reset billing address but keep the same country as shipping
      setBillingAddress({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zipCode: '',
        country: shippingAddress.country
      });
    }
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }

    setCart(cart.map(item => 
      item.id === productId 
        ? { ...item, quantity: newQuantity }
        : item
    ));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };
  
  // Get shop items total only (excludes print quotes for free shipping calculation)
  const getShopItemsTotal = () => {
    return cart
      .filter(item => item.type !== 'print_quote')
      .reduce((total, item) => total + (item.price * item.quantity), 0);
  };
  
  // Check if eligible for free shipping (only shop items count)
  const isEligibleForFreeShipping = () => {
    const shopTotal = getShopItemsTotal();
    return shopTotal >= 50;
  };

  const getCartItemCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      alert('Please log in to proceed with checkout');
      return;
    }

    // Show address form first
    if (!showAddressForm) {
      setShowAddressForm(true);
      return;
    }

    // Validate addresses
    const shippingErrors = validateAddress(shippingAddress, 'shipping');
    const billingErrors = sameAsShipping ? {} : validateAddress(billingAddress, 'billing');
    const allErrors = { ...shippingErrors, ...billingErrors };

    if (Object.keys(allErrors).length > 0) {
      setAddressErrors(allErrors);
      alert('Please fill in all required address fields');
      return;
    }

    setLoadingCheckout(true);

    try {
      // Get Firebase ID token
      const idToken = await getIdToken();
      if (!idToken) {
        alert('Authentication error. Please log in again.');
        return;
      }

      // Create cart checkout with Stripe
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      let apiUrl = backendUrl;
      if (apiUrl.includes('None') || !apiUrl || apiUrl === '') {
        apiUrl = '';
      }

      // For now, we'll create individual orders for each cart item
      // In a full implementation, you'd want a dedicated cart checkout endpoint
      const currentUrl = window.location.origin;
      const successUrl = `${currentUrl}/?payment=success&session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${currentUrl}/?payment=cancelled`;

      // Calculate total amount
      const totalAmount = getCartTotal();

      // Prepare checkout data with addresses
      const checkoutData = {
        cart_items: cart.map(item => ({
          id: item.id,
          product_id: item.id,
          name: item.name,
          manufacturer: item.manufacturer,
          category: item.category,
          quantity: item.quantity,
          price: item.price
        })),
        total_amount: totalAmount,
        success_url: successUrl,
        cancel_url: cancelUrl,
        shipping_address: shippingAddress,
        billing_address: sameAsShipping ? shippingAddress : billingAddress
      };

      const response = await fetch(`${apiUrl}/api/cart/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        credentials: 'include',
        body: JSON.stringify(checkoutData)
      });

      if (response.ok) {
        const result = await response.json();
        
        // Redirect to Stripe checkout
        if (result.checkout_url) {
          window.location.href = result.checkout_url;
        } else {
          throw new Error('No checkout URL received');
        }
      } else {
        const errorData = await response.json();
        alert(`Checkout error: ${errorData.detail || 'Please try again'}`);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Error processing checkout. Please try again.');
    } finally {
      setLoadingCheckout(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-center relative">
            <Button 
              onClick={navigateHome} 
              variant="outline"
              className="absolute left-0"
            >
              ← Back to Home
            </Button>
            
            {/* Centered Logo */}
            <button onClick={navigateHome} className="flex items-center">
              <img 
                src="/logo-nav.png" 
                alt="Create 3D Works Logo" 
                className="h-10 sm:h-12 w-auto object-contain"
                style={{ minWidth: '120px' }}
              />
            </button>
            
            <Button 
              onClick={navigateToProducts} 
              variant="outline"
              className="absolute right-0"
            >
              Continue Shopping
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {cart.length === 0 ? (
          /* Empty Cart */
          <div className="text-center py-12">
            <ShoppingCart className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-slate-600 mb-4">Your cart is empty</h2>
            <p className="text-slate-500 mb-6">Add some products to your cart to get started</p>
            <Button onClick={navigateToProducts} className="bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900">
              Start Shopping
            </Button>
          </div>
        ) : (
          /* Cart Contents */
          <div className="space-y-6">
            {/* Cart Header */}
            <div className="text-center">
              <h1 className="text-3xl font-bold text-slate-800 mb-2">Shopping Cart</h1>
              <p className="text-slate-600">{getCartItemCount()} items in your cart</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-6">
                {/* Cart Items List */}
                <div className="space-y-4">
                  {cart.map(item => (
                  <Card key={item.id} className="overflow-hidden">
                    <CardContent className="p-4">
                      <div className="flex items-start space-x-4">
                        {/* Product Image */}
                        <img
                          src={item.image_url}
                          alt={item.name}
                          className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                        />
                        
                        {/* Product Details */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h3 className="font-medium text-slate-800 truncate">{item.name}</h3>
                              
                              {/* Show description for print quotes, otherwise show manufacturer/category */}
                              {item.type === 'print_quote' && item.description ? (
                                <div className="text-sm text-slate-600 mt-1 space-y-0.5 whitespace-pre-line">
                                  {item.description}
                                </div>
                              ) : (
                                <>
                                  <p className="text-sm text-slate-600">{item.manufacturer}</p>
                                  <p className="text-sm text-slate-500">{item.category}</p>
                                </>
                              )}
                            </div>
                            <button
                              onClick={() => removeFromCart(item.id)}
                              className="text-slate-400 hover:text-red-500 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          
                          {/* Quantity and Price */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <Input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => updateQuantity(item.id, parseInt(e.target.value) || 1)}
                                className="w-16 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                min="1"
                              />
                              <button
                                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                            
                            <div className="text-right">
                              <div className="font-semibold text-lg">{formatPrice(item.price * item.quantity)}</div>
                              <div className="text-sm text-slate-500">{formatPrice(item.price)} each</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                </div>

                {/* Address Forms - Show when checkout is initiated */}
                {showAddressForm && (
                  <div className="space-y-6">
                    {/* Shipping Address */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Shipping Address</CardTitle>
                        <CardDescription>Where should we deliver your order?</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Country Selection */}
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Country *</label>
                          <select
                            value={shippingAddress.country}
                            onChange={(e) => setShippingAddress({...shippingAddress, country: e.target.value, state: '', zipCode: ''})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="United States">United States</option>
                            <option value="Canada">Canada</option>
                          </select>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">First Name *</label>
                            <Input
                              value={shippingAddress.firstName}
                              onChange={(e) => setShippingAddress({...shippingAddress, firstName: e.target.value})}
                              className={addressErrors.shippingFirstName ? 'border-red-500' : ''}
                            />
                            {addressErrors.shippingFirstName && (
                              <p className="text-red-500 text-xs mt-1">{addressErrors.shippingFirstName}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Last Name *</label>
                            <Input
                              value={shippingAddress.lastName}
                              onChange={(e) => setShippingAddress({...shippingAddress, lastName: e.target.value})}
                              className={addressErrors.shippingLastName ? 'border-red-500' : ''}
                            />
                            {addressErrors.shippingLastName && (
                              <p className="text-red-500 text-xs mt-1">{addressErrors.shippingLastName}</p>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                            <Input
                              type="email"
                              value={shippingAddress.email}
                              onChange={(e) => setShippingAddress({...shippingAddress, email: e.target.value})}
                              className={addressErrors.shippingEmail ? 'border-red-500' : ''}
                            />
                            {addressErrors.shippingEmail && (
                              <p className="text-red-500 text-xs mt-1">{addressErrors.shippingEmail}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Phone *</label>
                            <Input
                              type="tel"
                              value={shippingAddress.phone}
                              onChange={(e) => setShippingAddress({...shippingAddress, phone: e.target.value})}
                              className={addressErrors.shippingPhone ? 'border-red-500' : ''}
                            />
                            {addressErrors.shippingPhone && (
                              <p className="text-red-500 text-xs mt-1">{addressErrors.shippingPhone}</p>
                            )}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Address Line 1 *</label>
                          <Input
                            value={shippingAddress.address1}
                            onChange={(e) => setShippingAddress({...shippingAddress, address1: e.target.value})}
                            className={addressErrors.shippingAddress1 ? 'border-red-500' : ''}
                          />
                          {addressErrors.shippingAddress1 && (
                            <p className="text-red-500 text-xs mt-1">{addressErrors.shippingAddress1}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Address Line 2</label>
                          <Input
                            value={shippingAddress.address2}
                            onChange={(e) => setShippingAddress({...shippingAddress, address2: e.target.value})}
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">City *</label>
                            <Input
                              value={shippingAddress.city}
                              onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})}
                              className={addressErrors.shippingCity ? 'border-red-500' : ''}
                            />
                            {addressErrors.shippingCity && (
                              <p className="text-red-500 text-xs mt-1">{addressErrors.shippingCity}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              {shippingAddress.country === 'United States' ? 'State *' : 'Province *'}
                            </label>
                            <Input
                              value={shippingAddress.state}
                              onChange={(e) => setShippingAddress({...shippingAddress, state: e.target.value})}
                              className={addressErrors.shippingState ? 'border-red-500' : ''}
                              placeholder={shippingAddress.country === 'United States' ? 'e.g., CA' : 'e.g., ON'}
                            />
                            {addressErrors.shippingState && (
                              <p className="text-red-500 text-xs mt-1">{addressErrors.shippingState}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              {shippingAddress.country === 'United States' ? 'ZIP Code *' : 'Postal Code *'}
                            </label>
                            <Input
                              value={shippingAddress.zipCode}
                              onChange={(e) => setShippingAddress({...shippingAddress, zipCode: e.target.value})}
                              className={addressErrors.shippingZipCode ? 'border-red-500' : ''}
                              placeholder={shippingAddress.country === 'United States' ? '12345' : 'A1A 1A1'}
                            />
                            {addressErrors.shippingZipCode && (
                              <p className="text-red-500 text-xs mt-1">{addressErrors.shippingZipCode}</p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Billing Address */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Billing Address</CardTitle>
                        <CardDescription>Address for payment processing</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="sameAsShipping"
                            checked={sameAsShipping}
                            onChange={(e) => handleSameAsShipping(e.target.checked)}
                            className="rounded border-gray-300"
                          />
                          <label htmlFor="sameAsShipping" className="text-sm font-medium text-slate-700">
                            Same as shipping address
                          </label>
                        </div>

                        {!sameAsShipping && (
                          <div className="space-y-4">
                            {/* Country Selection */}
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">Country *</label>
                              <select
                                value={billingAddress.country}
                                onChange={(e) => setBillingAddress({...billingAddress, country: e.target.value, state: '', zipCode: ''})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              >
                                <option value="United States">United States</option>
                                <option value="Canada">Canada</option>
                              </select>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">First Name *</label>
                                <Input
                                  value={billingAddress.firstName}
                                  onChange={(e) => setBillingAddress({...billingAddress, firstName: e.target.value})}
                                  className={addressErrors.billingFirstName ? 'border-red-500' : ''}
                                />
                                {addressErrors.billingFirstName && (
                                  <p className="text-red-500 text-xs mt-1">{addressErrors.billingFirstName}</p>
                                )}
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Last Name *</label>
                                <Input
                                  value={billingAddress.lastName}
                                  onChange={(e) => setBillingAddress({...billingAddress, lastName: e.target.value})}
                                  className={addressErrors.billingLastName ? 'border-red-500' : ''}
                                />
                                {addressErrors.billingLastName && (
                                  <p className="text-red-500 text-xs mt-1">{addressErrors.billingLastName}</p>
                                )}
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                                <Input
                                  type="email"
                                  value={billingAddress.email}
                                  onChange={(e) => setBillingAddress({...billingAddress, email: e.target.value})}
                                  className={addressErrors.billingEmail ? 'border-red-500' : ''}
                                />
                                {addressErrors.billingEmail && (
                                  <p className="text-red-500 text-xs mt-1">{addressErrors.billingEmail}</p>
                                )}
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Phone *</label>
                                <Input
                                  type="tel"
                                  value={billingAddress.phone}
                                  onChange={(e) => setBillingAddress({...billingAddress, phone: e.target.value})}
                                  className={addressErrors.billingPhone ? 'border-red-500' : ''}
                                />
                                {addressErrors.billingPhone && (
                                  <p className="text-red-500 text-xs mt-1">{addressErrors.billingPhone}</p>
                                )}
                              </div>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">Address Line 1 *</label>
                              <Input
                                value={billingAddress.address1}
                                onChange={(e) => setBillingAddress({...billingAddress, address1: e.target.value})}
                                className={addressErrors.billingAddress1 ? 'border-red-500' : ''}
                              />
                              {addressErrors.billingAddress1 && (
                                <p className="text-red-500 text-xs mt-1">{addressErrors.billingAddress1}</p>
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">Address Line 2</label>
                              <Input
                                value={billingAddress.address2}
                                onChange={(e) => setBillingAddress({...billingAddress, address2: e.target.value})}
                              />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">City *</label>
                                <Input
                                  value={billingAddress.city}
                                  onChange={(e) => setBillingAddress({...billingAddress, city: e.target.value})}
                                  className={addressErrors.billingCity ? 'border-red-500' : ''}
                                />
                                {addressErrors.billingCity && (
                                  <p className="text-red-500 text-xs mt-1">{addressErrors.billingCity}</p>
                                )}
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">
                                  {billingAddress.country === 'United States' ? 'State *' : 'Province *'}
                                </label>
                                <Input
                                  value={billingAddress.state}
                                  onChange={(e) => setBillingAddress({...billingAddress, state: e.target.value})}
                                  className={addressErrors.billingState ? 'border-red-500' : ''}
                                  placeholder={billingAddress.country === 'United States' ? 'e.g., CA' : 'e.g., ON'}
                                />
                                {addressErrors.billingState && (
                                  <p className="text-red-500 text-xs mt-1">{addressErrors.billingState}</p>
                                )}
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">
                                  {billingAddress.country === 'United States' ? 'ZIP Code *' : 'Postal Code *'}
                                </label>
                                <Input
                                  value={billingAddress.zipCode}
                                  onChange={(e) => setBillingAddress({...billingAddress, zipCode: e.target.value})}
                                  className={addressErrors.billingZipCode ? 'border-red-500' : ''}
                                  placeholder={billingAddress.country === 'United States' ? '12345' : 'A1A 1A1'}
                                />
                                {addressErrors.billingZipCode && (
                                  <p className="text-red-500 text-xs mt-1">{addressErrors.billingZipCode}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Address Form Actions */}
                    <div className="flex justify-between">
                      <Button 
                        onClick={() => setShowAddressForm(false)}
                        variant="outline"
                      >
                        Back to Cart
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Cart Summary */}
              <div className="lg:col-span-1">
                <Card className="sticky top-24">
                  <CardHeader>
                    <CardTitle>Order Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Subtotal ({getCartItemCount()} items):</span>
                        <span>{formatPrice(getCartTotal())}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Shipping:</span>
                        <span>{isEligibleForFreeShipping() ? 'FREE' : '$9.99'}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Tax:</span>
                        <span>{formatPrice(getCartTotal() * 0.08)}</span>
                      </div>
                    </div>
                    
                    <Separator />
                    
                    <div className="flex justify-between font-semibold text-lg">
                      <span>Total:</span>
                      <span>{formatPrice(getCartTotal() + (isEligibleForFreeShipping() ? 0 : 9.99) + (getCartTotal() * 0.08))}</span>
                    </div>

                    {!isEligibleForFreeShipping() && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-sm text-blue-700">
                          Add {formatPrice(50 - getShopItemsTotal())} more in shop products for free shipping!
                        </p>
                        <p className="text-xs text-blue-600 mt-1">
                          *Free shipping applies to shop products only
                        </p>
                      </div>
                    )}

                    <Button 
                      onClick={handleCheckout}
                      disabled={loadingCheckout}
                      className="w-full bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 py-3"
                    >
                      {loadingCheckout ? 'Processing...' : `Checkout - ${formatPrice(getCartTotal() + (isEligibleForFreeShipping() ? 0 : 9.99) + (getCartTotal() * 0.08))}`}
                    </Button>

                    {!isAuthenticated && (
                      <p className="text-center text-sm text-slate-500">
                        🔒 Sign in required to proceed with checkout
                      </p>
                    )}

                    <div className="text-center">
                      <Button onClick={navigateToProducts} variant="outline" className="w-full">
                        Continue Shopping
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default CartPage;